import React, { useCallback, useEffect, useMemo, useRef } from 'react'
// import './styles/notification-ecomm-options.scss'
import { Well } from '@pushly/aqe/lib/components'
import { Select, Skeleton, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import {
    CatalogItemPickStrategy,
    CatalogItemType,
    CombinedItemStrategy,
    getRankingStrategyOptions,
    getSubscriberScopedOptions,
    isCatalogItemPickStrategy,
    isItemRankingStrategy,
} from '@pushly/models/lib/enums/catalogs'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { ActionDispatcher } from '../types'
import { reconcileSelectedCombinedStrategy } from 'src/features/journeys/selectors/catalog-item-pick-strategy'

interface INotificationCatalogItemOptionsProps {
    className?: string
    options: CombinedItemStrategy[]
    defaultSelectedStrategy?: CombinedItemStrategy
    builder: INotificationBuilderState
    dispatchChanges: ActionDispatcher
    loading?: boolean
    disabled?: boolean
    itemType?: CatalogItemType
    triggerType?: TriggerType
}

function arraysShallowEqual<T>(a: T[], b: T[]) {
    return a.length === b.length && a.every((v, i) => v === b[i])
}

const NotificationCatalogItemOptions = (props: INotificationCatalogItemOptionsProps) => {
    const {
        loading,
        builder,
        disabled,
        options: propsOptions,
        defaultSelectedStrategy,
        itemType,
        triggerType,
        dispatchChanges,
    } = props

    const stateOptions = builder.catalogItemPickStrategyOptions
    const stateSelectedStrategy = builder.selectedCatalogItemPickStrategy

    // Track whether initial mount sync has completed to prevent
    // the effect from overriding user selections with stale prop values.
    const initialSyncDone = useRef(false)

    const setOptions = useCallback(
        (nextOptions: CombinedItemStrategy[]) => {
            dispatchChanges({
                type: 'patch',
                entity: 'root',
                data: {
                    catalogItemPickStrategyOptions: nextOptions,
                },
            })
        },
        [dispatchChanges],
    )

    const setSelection = useCallback(
        (nextSelection: CombinedItemStrategy) => {
            dispatchChanges({
                type: 'patch',
                entity: 'root',
                data: {
                    selectedCatalogItemPickStrategy: nextSelection,
                },
            })
        },
        [dispatchChanges],
    )

    // Sync options and default selection on mount only.
    // After initial sync, user changes go directly through handleCatalogStrategyChange.
    useEffect(() => {
        if (initialSyncDone.current) return
        if (!propsOptions.length) return

        if (!arraysShallowEqual(stateOptions ?? [], propsOptions)) {
            setOptions(propsOptions)
        }

        if (defaultSelectedStrategy) {
            const nextSelection = reconcileSelectedCombinedStrategy(propsOptions, defaultSelectedStrategy)
            if (nextSelection && nextSelection !== stateSelectedStrategy) {
                setSelection(nextSelection)
            }
        }

        initialSyncDone.current = true
    }, [propsOptions, stateOptions, defaultSelectedStrategy, stateSelectedStrategy, setOptions, setSelection])

    const handleCatalogStrategyChange = useCallback(
        (strategy: CombinedItemStrategy) => {
            const nextSelection = reconcileSelectedCombinedStrategy(stateOptions ?? [], strategy)
            if (nextSelection) {
                setSelection(nextSelection)
            }
        },
        [stateOptions, setSelection],
    )

    const hasContextStrategies = propsOptions.some((opt) => isCatalogItemPickStrategy(opt))
    const hasRankingStrategies = propsOptions.some((opt) => isItemRankingStrategy(opt))

    const scopedOptions = useMemo(() => {
        if (hasContextStrategies) {
            const contextStrategies = propsOptions.filter((opt) =>
                isCatalogItemPickStrategy(opt),
            ) as CatalogItemPickStrategy[]
            return getSubscriberScopedOptions(contextStrategies, triggerType, itemType)
        }

        if (hasRankingStrategies) {
            return getRankingStrategyOptions(itemType)
        }

        return []
    }, [propsOptions, itemType, triggerType, hasContextStrategies, hasRankingStrategies])

    const optionsByGroup = useMemo(() => {
        const grouped: Record<string, typeof scopedOptions> = {}
        scopedOptions.forEach((opt) => {
            if (!grouped[opt.group]) {
                grouped[opt.group] = []
            }
            grouped[opt.group].push(opt)
        })
        return grouped
    }, [scopedOptions])

    if (!stateOptions || stateOptions.length === 0) {
        return null
    }

    const isValidSelection = scopedOptions.some((opt) => opt.value === stateSelectedStrategy)
    const safeValue = isValidSelection ? stateSelectedStrategy : undefined

    return (
        <Well
            className={getClassNames('notification-ecomm-options-wrapper', {})}
            title={
                <>
                    <span>Item Selection</span>
                    <Tooltip
                        title={
                            <span>
                                Determines how the item that will be included within the notification will be chosen.
                            </span>
                        }
                    >
                        <QuestionCircleOutlined className="info-icon" />
                    </Tooltip>
                </>
            }
            mode="ghost"
            hideFooter={true}
        >
            <Skeleton loading={loading} active={true} avatar={false} title={false}>
                <div className="item-selection-controls">
                    <div className="item-selection-metric">
                        <Select
                            className="ecomm-item-pick-strategy-select"
                            popupClassName="ecomm-item-pick-strategy-dropdown"
                            placeholder="Select how to choose the item"
                            value={safeValue}
                            onChange={handleCatalogStrategyChange}
                            disabled={disabled}
                            style={{ width: '100%' }}
                        >
                            {Object.entries(optionsByGroup).map(([group, groupOpts]) => (
                                <Select.OptGroup key={group} label={group}>
                                    {groupOpts.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                            ))}
                        </Select>
                    </div>
                </div>
            </Skeleton>
        </Well>
    )
}

export default NotificationCatalogItemOptions
